/**
 * Ops AI Workspace (v650) — mast chrome · interrupt dock · depth pass.
 *
 * Soft≠Live · Page230 HITL seat floor (never Soft-only hide Hold Live).
 */
import { useMemo } from "react";
import { html, Ico, go } from "./ui.js";
import { path, useApi } from "./api.js";
import { isLocalHost } from "./pathseal.js";
import { useDecisionHome, actionLabel } from "./ai-context-rail.js";
import { autonomyPlanesOf, fourVisiblesOf } from "./autonomy-planes.js";
import { TechnicalDetails } from "./layered-console.js";
import { closeWaitCopy } from "./ops-map-ai-layers.js";
import { boardHeadingOf, operatorSay, whySay } from "./operator-say.js";

/** Soft≠Live Page230: missing contract ops must not crash cockpit. */
export function safeOpsAiPath(op, opts) {
  try {
    return path(op, opts);
  } catch (_e) {
    if (op === "get__ops_ai_status") return "/ops/ai/status";
    if (op === "get__ai_home-context") return "/ai/home-context";
    return "";
  }
}

/** Operator seat label — never show engineer detail codes or wire paths. */
export function softOpsAiSeatLabelOf(seat, opts = {}) {
  if (opts.error) return "Could not confirm AI access · try Refresh";
  if (opts.loading) return "Confirming AI seat…";
  if (seat && seat.postureLabel) return String(seat.postureLabel);
  const d = String((seat && seat.detail) || "").toLowerCase();
  if (d.includes("user_floor")) return "You can watch AI turns · interrupt stays for managers";
  if (d.includes("staff_floor")) return "You can watch · interrupt stays for site managers";
  if (d.includes("hitl")) return "Watching open · interrupt closed";
  if (d.includes("context") || d.includes("all_sealed")) return "AI desk closed for this seat";
  if (seat && seat.hitlAllowed && seat.contextAllowed) return "Watching and interrupt open";
  if (seat && seat.contextAllowed) return "Watching open · interrupt closed";
  if (seat && seat.enabled === false) return "AI desk closed for this seat";
  return "AI seat ready";
}

/** Soft operator error — never paint wire paths or codes. */
export function softOpsAiErrorOf(err) {
  const n = String(err == null ? "" : err).trim().toLowerCase();
  if (!n) return "That AI action could not be completed.";
  if (/\b403\b|forbidden|insufficient|hitl|manager|role/.test(n)) {
    return "Your account cannot interrupt this plan. Ask a manager.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(n)) {
    return "Sign in again to continue.";
  }
  if (/\b404\b|not found|no longer waiting/.test(n)) {
    return "That plan is no longer waiting.";
  }
  if (/\b429\b|too many/.test(n)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable|offline|disabled/.test(n)) {
    return "Decision service is temporarily offline.";
  }
  if (/network|failed to fetch|timeout/.test(n)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/ai\/|\/ops\/|http|GET |POST |status_path/.test(n)) {
    return "That AI action could not be completed.";
  }
  return "That AI action could not be completed.";
}

function watchLineOf(planes, fallback) {
  const p = planes && typeof planes === "object" ? planes : {};
  if (p.skill_live && p.remux_media_played) return "Live at the point";
  if (p.skill_live) return "Pulling live";
  if (p.skill_snapshot) return "Snapshot on this event";
  if (p.skill_record) return "Recording this event";
  const raw = String(fallback || "").trim();
  if (!raw) return "Watching this event";
  if (/[_/]|quota|ack|flag|ledger|remux|intent|tombstone/i.test(raw)) return "Watching this event";
  return raw.slice(0, 80);
}

/** Pure: seat floor from Live Ops AI posture (Page230). Fail-closed on load error. */
export function opsAiSeatOf(post, opts = {}) {
  if (opts.error) {
    return {
      enabled: false,
      armed: false,
      contextAllowed: false,
      hitlAllowed: false,
      reReadAllowed: true,
      softFleetLocalOnly: false,
      role: String(opts.role || ""),
      detail: "ops_ai_status_error",
      postureLabel: softOpsAiSeatLabelOf(null, { error: true }),
    };
  }
  if (opts.loading) {
    return {
      enabled: false,
      armed: false,
      contextAllowed: false,
      hitlAllowed: false,
      reReadAllowed: false,
      softFleetLocalOnly: false,
      role: String(opts.role || ""),
      detail: "ops_ai_status_loading",
      postureLabel: softOpsAiSeatLabelOf(null, { loading: true }),
    };
  }
  const p = post && typeof post === "object" ? post : {};
  const seat = {
    enabled: p.enabled === true,
    armed: p.armed === true,
    contextAllowed: (p.context_plane_allowed === true),
    hitlAllowed: (
      p.hitl_allowed === true
      || p.hitl_plane_allowed === true
      || p.decision_plane_allowed === true
    ),
    reReadAllowed: (p.re_read_allowed === true),
    softFleetLocalOnly:
      (p.soft_fleet_local_only === true),
    role: String(p.principal_role || opts.role || ""),
    detail: p.detail || null,
    postureLabel: p.posture_label || null,
  };
  seat.postureLabel = softOpsAiSeatLabelOf(seat);
  return seat;
}

/** hold first · approve last — AI auto-exec SoT. */
export function interruptFirstActionsOf(actions) {
  const list = Array.isArray(actions) ? actions.slice() : [];
  const rank = (id) => {
    const k = String(id || "");
    if (k === "hold") return 0;
    if (k === "escalate") return 1;
    if (k === "more_evidence") return 2;
    if (k === "approve") return 9;
    return 5;
  };
  return list.sort((a, b) => rank(a && a.id) - rank(b && b.id));
}

export function opsAiModeOf({ waiting, waitingN, softActiveN }) {
  const n = Math.max(Number(waitingN) || 0, Number(softActiveN) || 0);
  if (n >= 3) return "swarm";
  if (waiting || n >= 1) return "command";
  return "watch";
}

/** One coherent plane — Soft mission never shows as "AI off". */
export function opsFocusPlaneOf({ local, softN, waiting, aiOn }) {
  if (waiting) return "live";
  if (local && softN > 0) return "soft";
  if (aiOn === false) return "off";
  return "idle";
}

function softActiveOf(fleet) {
  const st = fleet && fleet.state;
  if (!st) return { n: 0, missions: [], free: 0, total: 0 };
  const missions = (st.missions || []).filter(
    (m) => m && m.status !== "done" && m.status !== "cancelled",
  );
  const assets = st.assets || [];
  const free = assets.filter((a) => a && a.free).length;
  return { n: missions.length, missions: missions.slice(0, 5), free, total: assets.length };
}

function unitsOf(m) {
  return (m && m.assigned ? m.assigned : [])
    .map((x) => x && x.id)
    .filter(Boolean)
    .slice(0, 3);
}

function shortActLabel(a) {
  const raw = String(actionLabel(a) || a?.id || "")
    .replace(/\s*·\s*Live/gi, "")
    .trim();
  if (a && a.id === "escalate") return "Escalate";
  if (a && a.id === "more_evidence") return "Evidence";
  /* Approve ritual is not painted. Continue ≠ Approve. */
  if (a && (a.id === "continue" || a.id === "approve")) return "Continue";
  return raw.length > 16 ? raw.slice(0, 15) + "…" : raw;
}

/**
 * @param {{
 *   toast?: function,
 *   fleet?: any,
 *   seat?: any,
 *   onFocusDevice?: function,
 *   onResolved?: function,
 *   onOpenObserve?: function,
 *   onHide?: function,
 * }} props
 */
export function OpsAiWorkspace({
  toast,
  fleet = null,
  seat = null,
  onFocusDevice,
  onResolved,
  onOpenObserve,
  onHide,
}) {
  const home = useDecisionHome();
  const { maic, pulse, waiting, actions, busy, why, act, aiOn, reload, data } = home;
  const auto = data && data.autonomy && typeof data.autonomy === "object" ? data.autonomy : null;
  const planes = autonomyPlanesOf(auto);
  const four = fourVisiblesOf(planes, { loading: !data || !auto });
  const local = isLocalHost();
  const soft = useMemo(() => softActiveOf(local ? fleet : null), [local, fleet && fleet.state]);
  const mode = opsAiModeOf({
    waiting,
    waitingN: maic.waiting_n,
    softActiveN: soft.n,
  });
  const plane = opsFocusPlaneOf({
    local,
    softN: soft.n,
    waiting,
    aiOn,
  });
  const driving = plane === "live" || plane === "soft";

  const posture = useApi(safeOpsAiPath("get__ops_ai_status"));
  const aiSeat = opsAiSeatOf(posture.data, {
    loading: !!posture.loading && !posture.data,
    error: !!posture.error && !posture.data,
  });
  const hitlFromStatus =
    !posture.loading && posture.data && typeof posture.data === "object"
      ? !!aiSeat.hitlAllowed
      : null;
  /* Backend status SoT when present; never open HITL from FE map seat alone. */
  const hitlOk =
    hitlFromStatus != null
      ? hitlFromStatus
      : (seat ? !!(seat.hitlAllowed || seat.decisionAllowed) : false);
  const ordered = interruptFirstActionsOf(actions);
  const holdAct = ordered.find((a) => a && a.id === "hold") || null;
  const otherActs = ordered.filter((a) => a && a.id !== "hold");
  const deviceId = maic.device_id || "";

  const softLead = soft.missions[0] || null;
  const softUnits = unitsOf(softLead);

  const driveVerb =
    plane === "live"
      ? mode === "swarm"
        ? "Team Runtime · multi"
        : "Team Runtime"
      : plane === "soft"
        ? soft.n > 1
          ? `Practice · ${soft.n}`
          : "Practice desk"
        : plane === "off"
          ? "Assist off"
          : "Standby";

  const liveTitle = operatorSay(maic.lead_title || maic.category || "Open plan", "Open plan");
  const focusTitle =
    plane === "live"
      ? (maic.risk_independent === true ? liveTitle : (liveTitle + " · unproven"))
      : plane === "soft"
        ? operatorSay((softLead && (softLead.title || softLead.place)) || "Mission", "Mission")
        : plane === "off"
          ? "No assist yet"
          : "Watch is quiet";

  const focusMeta =
    plane === "soft"
      ? softUnits.length
        ? softUnits.filter((u) => u && !String(u).match(/^[a-z0-9-]{12,}$/i)).join(" · ") || "Practice desk"
        : soft.total
          ? `${soft.free} free · ${soft.total} fleet`
          : "Practice desk"
      : plane === "live"
        ? (why ? whySay(why) : "Open turn")
        : plane === "off"
          ? local
            ? "Map open · practice desk available here"
            : "Map open · no AI turn"
          : "AI watching the plant";

  const pulseLine = (() => {
    const p = String(pulse || "watching");
    if (p === "needs_you") return "Needs you";
    if (p === "watching") return driving ? "In motion" : "Watching";
    return operatorSay(p, driving ? "In motion" : "Watching");
  })();

  const moreSoft = plane === "soft" ? soft.missions.slice(1) : [];
  const envLabel = local ? "Lab" : "Site";

  const runLive = async (action) => {
    if (!action) return;
    if (!hitlOk) {
      toast && toast(
        softOpsAiSeatLabelOf(aiSeat) || "Your account cannot interrupt this plan.",
        "warn",
      );
      return;
    }
    const out = await act(action, toast);
    if (out) onResolved && onResolved(action.id, out);
  };

  const softHold = () => {
    try {
      if (fleet && fleet.humanStopAll) fleet.humanStopAll("ops_ai_v650_hold");
      toast && toast("Practice turn held", "warn");
    } catch (e) {
      toast && toast(softOpsAiErrorOf(e), "bad");
    }
  };

  const softResume = () => {
    try {
      if (fleet && fleet.humanResumeAi) fleet.humanResumeAi();
      toast && toast("Practice turn resumed", "go");
    } catch (e) {
      toast && toast(softOpsAiErrorOf(e), "bad");
    }
  };

  const softFocus = (m) => {
    const id = (m && m.assigned && m.assigned[0] && m.assigned[0].id) || "";
    if (!id) return;
    try {
      window.dispatchEvent(new CustomEvent("croc-soft-focus", { detail: { id } }));
    } catch {
      /* */
    }
  };

  const refresh = () => {
    try {
      posture.reload && posture.reload();
    } catch {
      /* */
    }
    reload && reload();
  };

  const softBusy = Math.max(0, (soft.total || 0) - (soft.free || 0));
  const turnN = plane === "soft" ? soft.n : waiting ? Math.max(1, Number(maic.waiting_n) || 1) : 0;

  return html`<div
    className="ops-cockpit"
    data-field="v650"
    data-plane=${plane}
    data-mode=${mode}
    data-drive=${driving ? "1" : "0"}
    data-live=${waiting ? "1" : "0"}
    data-soft=${local && soft.n ? "1" : "0"}
    data-ai=${aiOn === false ? "0" : "1"}
    data-hitl=${hitlOk ? "1" : "0"}
    data-cmc-residual="ops-ai-v610"
    data-cmc-page="page230-ops-ai">

    <header className="ops-cockpit__top">
      <div className="ops-cockpit__mast">
        <div className="ops-cockpit__mode" role="tablist" aria-label="Ops face">
          <i className="ops-cockpit__glider" data-on="ai" aria-hidden="true"/>
          <span className="ops-cockpit__face" data-on="1" role="tab" aria-selected="true">
            <i className="ops-cockpit__face-pulse" aria-hidden="true"/>
            <b>AI</b>
          </span>
          <button type="button" className="ops-cockpit__face" role="tab" aria-selected="false"
            onClick=${() => (onOpenObserve ? onOpenObserve() : go("/ops/devices"))}>
            <b>Observe</b>
          </button>
        </div>
        <div className="ops-cockpit__mast-end">
          <span className="ops-cockpit__env" data-local=${local ? "1" : "0"} data-drive=${driving ? "1" : "0"}>
            <i className="ops-cockpit__env-dot" aria-hidden="true"/>
            <em>${envLabel}</em>
          </span>
          ${onHide
            ? html`<button type="button" className="ops-cockpit__hide" title="Hide panel"
                aria-label="Hide panel" onClick=${onHide}>
                <${Ico} d="arrowR" w=${14}/>
              </button>`
            : null}
        </div>
      </div>
    </header>

    <section className="ops-cockpit__stage" aria-label="AI drive" aria-live="polite">
      <div className="ops-cockpit__hero" data-plane=${plane} data-drive=${driving ? "1" : "0"}>
        <div className="ops-cockpit__core" aria-hidden="true">
          <i className="ops-cockpit__ring ops-cockpit__ring--a"/>
          <i className="ops-cockpit__ring ops-cockpit__ring--b"/>
          <i className="ops-cockpit__ring ops-cockpit__ring--c"/>
          <b className="ops-cockpit__core-dot"/>
          <span className="ops-cockpit__scan"/>
        </div>
        <div className="ops-cockpit__drive">
          <span className="ops-cockpit__drive-k">${driveVerb}</span>
          <span className="ops-cockpit__bus" data-hot=${pulse === "needs_you" ? "1" : "0"}>
            ${pulseLine}
          </span>
        </div>
        <h2 className="ops-cockpit__title" title=${focusTitle}>${focusTitle}</h2>
        <p className="ops-cockpit__meta-line">${watchLineOf(planes, focusMeta)}</p>
        ${planes.skill_live || planes.skill_snapshot || planes.skill_record || planes.skill_close
          ? html`<ol className="ops-cockpit__loop" aria-label="What is on this event" data-cmc-signal-loop="1">
              <li data-on=${Number(planes.playing_n) > 0 || (planes.skill_live && planes.remux_media_played) ? "1" : (planes.skill_live || Number(planes.live_n) > 0 ? "wait" : "0")}>${Number(planes.live_n) > 1 ? `${Number(planes.playing_n) || 0} of ${Number(planes.live_n)} live` : "Live"}</li>
              <li data-on=${planes.skill_snapshot ? "1" : "0"}>Snapshot</li>
              <li data-on=${planes.skill_record ? "1" : "0"}>Record</li>
              <li data-on=${planes.skill_close || planes.close_ready ? "1" : (planes.close_wait ? "wait" : "0")}>Close</li>
            </ol>`
          : null}
        ${Number(planes.live_n) > 1
          ? html`<p className="ops-cockpit__meta-line" data-cmc-live-seats="1">${Number(planes.closed_n) || 0} of ${Number(planes.live_n)} live cameras have proof</p>`
          : null}
        ${planes.close_wait && !planes.close_ready
          ? html`<p className="ops-cockpit__meta-line" data-cmc-close-wait=${planes.close_wait}>
              ${closeWaitCopy(planes.close_wait)}</p>`
          : null}
        ${planes.fault
          ? html`<p className="ops-cockpit__meta-line" data-cmc-autonomy-fault=${planes.reason || "1"}>
              AI is not running on this event</p>`
          : (!data || !auto || planes.inspect_unavailable)
            ? html`<p className="ops-cockpit__meta-line" data-cmc-autonomy-inspect="wait">Confirming this event…</p>`
          : planes.manual_only
            ? html`<p className="ops-cockpit__meta-line" data-cmc-autonomy-manual="1">AI is off on this site</p>`
            : null}
        <${TechnicalDetails} title="Proof & detail">
          <div className="ops-cockpit__metrics" data-cmc-autonomy-planes="1"
              aria-label="Configured vs Effective vs Health"
              data-configured=${planes.configured ? "1" : "0"}
              data-manual-only=${planes.manual_only ? "1" : "0"}
              data-health=${planes.runtime_health || ""}
              data-ledger=${planes.ledger || ""}
              data-fault=${planes.fault ? "1" : "0"}>
            <div className="ops-cockpit__m" title="How this site should run">
              <b>${planes.configured ? operatorSay(planes.configured_mode || "—", "—") : "Off"}</b><span>How it should run</span>
            </div>
            <div className="ops-cockpit__m" title="How it runs now">
              <b>${operatorSay(planes.effective_mode || "—", "—")}</b><span>How it runs</span>
            </div>
            <div className="ops-cockpit__m" title="Runtime health">
              <b>${operatorSay(planes.runtime_health || "—", "—")}</b><span>Health</span>
            </div>
            <div className="ops-cockpit__m" title="TeamRun ledger">
              <b>${operatorSay(planes.ledger || "—", "—")}</b><span>Record</span>
            </div>
          </div>
          <div className="ops-cockpit__metrics" data-cmc-four-visibles="1"
              data-cmc-audience="tenant-plus-global-rails"
              data-origin=${four.origin || planes.origin_source || ""}
              aria-label="Desired vs Effective · revision · ACK · why blocked">
            <div className="ops-cockpit__m" title="Tenant Autonomy mode vs effective">
              <b>${operatorSay(four.desired, four.desired || "—")}</b><span data-cmc-desired-effective="desired vs effective">How it should run</span>
            </div>
            <div className="ops-cockpit__m" title="Tenant policy revision · platform flag revision">
              <b>${four.rev}</b><span>Rule version</span>
            </div>
            <div className="ops-cockpit__m" title="Whether boards have answered">
              <b>${operatorSay(four.ack, four.ack || "—")}</b><span data-cmc-ack-lag="ACK / lag">Boards answered</span>
            </div>
            <div className="ops-cockpit__m" title="Why blocked">
              <b>${whySay(four.why)}</b><span>Why blocked</span>
            </div>
          </div>
          ${planes.team_n
            ? html`<p className="ops-cockpit__meta-line" data-cmc-team-roles=${planes.team_n}>
                ${planes.team_roles.join(" · ")}</p>`
            : null}
        <//>
        ${plane === "live"
          ? html`<p className="ops-cockpit__meta-line ops-cockpit__contract-hint">
              You can Hold this event at any time.
            </p>`
          : null}
        <div className="ops-cockpit__progress" aria-hidden="true">
          <span className="ops-cockpit__progress-bar"/>
        </div>

        <div className="ops-cockpit__metrics" aria-label="Fleet status">
          ${plane === "soft" && soft.total
            ? html`
                <div className="ops-cockpit__m" title="Boards free for a job">
                  <b>${soft.free}</b><span>free</span>
                </div>
                <div className="ops-cockpit__m" title="Boards on a job">
                  <b>${softBusy}</b><span>on a job</span>
                </div>
                <div className="ops-cockpit__m" title="Open jobs">
                  <b>${soft.n}</b><span>jobs</span>
                </div>
                <div className="ops-cockpit__m" title="Boards on this site">
                  <b>${soft.total}</b><span>boards</span>
                </div>`
            : plane === "live"
              ? html`
                  <div className="ops-cockpit__m" title="Plans waiting">
                    <b>${turnN}</b><span>open</span>
                  </div>
                  <div className="ops-cockpit__m ops-cockpit__m--wide" title="Board on this event">
                    <b>${deviceId ? boardHeadingOf(deviceId) : "—"}</b><span>board</span>
                  </div>`
              : html`
                  <div className="ops-cockpit__m">
                    <b>—</b><span>turns</span>
                  </div>
                  <div className="ops-cockpit__m">
                    <b>${aiOn === false ? "off" : "on"}</b><span>assist</span>
                  </div>`}
        </div>

        <div className="ops-cockpit__tools">
          ${deviceId || (plane === "soft" && softUnits[0])
            ? html`<button type="button" className="ops-cockpit__link"
                onClick=${() => {
                  if (plane === "soft" && softLead) softFocus(softLead);
                  else if (deviceId) onFocusDevice && onFocusDevice(deviceId);
                }}>
                Show on map</button>`
            : null}
          <button type="button" className="ops-cockpit__link" onClick=${() => refresh()}>
            Refresh
          </button>
        </div>
      </div>

      ${moreSoft.length
        ? html`<div className="ops-cockpit__queue" aria-label="More turns">
            <div className="ops-cockpit__queue-hd">
              <span>Also running</span>
              <em>${moreSoft.length}</em>
            </div>
            <ul className="ops-cockpit__queue-list">
              ${moreSoft.map((m, i) => html`
                <li key=${m.id} style=${{ "--i": String(i) }}>
                  <button type="button" className="ops-cockpit__queue-row"
                    onClick=${() => softFocus(m)}>
                    <i className="ops-cockpit__queue-pulse" aria-hidden="true"/>
                    <b title=${m.title || m.place || m.id}>${m.title || m.place || m.id}</b>
                    <u>${unitsOf(m).join(" · ") || "—"}</u>
                  </button>
                </li>`)}
            </ul>
          </div>`
        : null}
    </section>

    <footer className="ops-cockpit__human" aria-label="Interrupt">
      ${!hitlOk
        ? html`<p className="ops-cockpit__seal"
            title=${aiSeat.postureLabel || ""}>
            ${aiSeat.postureLabel || "Interrupt closed for this seat"}</p>`
        : null}
      ${posture.error && !posture.data
        ? html`<p className="ops-cockpit__seal">Could not confirm AI access · try Refresh</p>`
        : null}

      <div className="ops-cockpit__dock">
        ${holdAct
          ? html`<button type="button" className="ops-cockpit__hold"
              disabled=${busy || !hitlOk}
              onClick=${() => runLive(holdAct)}>
              <span className="ops-cockpit__hold-k">Hold</span>
              <span className="ops-cockpit__hold-v">This plan</span>
            </button>`
          : null}
        ${local
          ? html`<button type="button" className="ops-cockpit__hold ops-cockpit__hold--soft"
              data-primary=${plane === "soft" && !holdAct ? "1" : "0"}
              data-drive=${driving ? "1" : "0"}
              onClick=${softHold}>
              <span className="ops-cockpit__hold-k">Hold</span>
              <span className="ops-cockpit__hold-v">Practice</span>
            </button>`
          : null}
        ${!holdAct && !local
          ? html`<div className="ops-cockpit__idle">Nothing to hold right now</div>`
          : null}

        <div className="ops-cockpit__dock-side">
          ${local && soft.n
            ? html`<button type="button" className="ops-cockpit__ghost" onClick=${softResume}>
                Resume</button>`
            : null}
          ${otherActs.map((a) => html`
            <button key=${a.id} type="button" className="ops-cockpit__ghost"
              title=${actionLabel(a)}
              disabled=${busy || !hitlOk}
              onClick=${() => runLive(a)}>
              ${shortActLabel(a)}</button>`)}
        </div>
      </div>

      <button type="button" className="ops-cockpit__observe"
        onClick=${() => (onOpenObserve ? onOpenObserve() : go("/ops/devices"))}>
        <span className="ops-cockpit__observe-k">Archive</span>
        <span className="ops-cockpit__observe-v">Plant · cams · jobs</span>
        <${Ico} d="arrowR" w=${12}/>
      </button>
    </footer>
  </div>`;
}
